import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { API } from "@/lib/api";
import {
  ArrowLeft, DownloadSimple, EnvelopeSimple, WhatsappLogo, ShareNetwork,
  Copy as CopyIcon, Trash, Printer, CheckCircle, Receipt, ArrowCounterClockwise,
  UploadSimple, X, PencilSimple, ClockCounterClockwise,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

function fmt(n) { return Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

export default function InvoiceView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [pdfUrl, setPdfUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [emailLog, setEmailLog] = useState([]);
  const [versions, setVersions] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [snapshotView, setSnapshotView] = useState(null);  // {version_no, snapshot}
  const [paidOpen, setPaidOpen] = useState(false);
  const [paidForm, setPaidForm] = useState({ transaction_id: "", payment_date: "" });
  const [paidFile, setPaidFile] = useState(null);  // File | null
  const [paidFilePreview, setPaidFilePreview] = useState("");
  const [markingPaid, setMarkingPaid] = useState(false);
  const [unmarkBusy, setUnmarkBusy] = useState(false);

  useEffect(() => {
    if (!paidFile) {

      // eslint-disable-next-line
      setPaidFilePreview("");
      return;
    }
    const url = URL.createObjectURL(paidFile);
    setPaidFilePreview(url);
    return () => URL.revokeObjectURL(url);
  }, [paidFile]);

  const loadEmailLog = async () => {
    try {
      const { data } = await api.get(`/invoices/${id}/emails`);
      setEmailLog(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load email log:", err);
    }
  };

  const loadVersions = async () => {
    setHistoryLoading(true);
    try {
      const { data } = await api.get(`/invoices/${id}/versions`);
      setVersions(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load history");
    } finally {
      setHistoryLoading(false);
    }
  };

  const openHistory = async () => {
    setHistoryOpen(true);
    await loadVersions();
  };

  const viewSnapshot = async (versionNo) => {
    try {
      const { data } = await api.get(`/invoices/${id}/versions/${versionNo}`);
      setSnapshotView(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load snapshot");
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/invoices/${id}`);
      setInvoice(data);
      setEmailTo(data.customer?.email || "");
      const r = await api.get(`/invoices/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      setPdfUrl(url);
      await loadEmailLog();
    } catch (err) {
      console.error(err);
      toast.error("Failed to load invoice");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    // eslint-disable-next-line
    load();
    return () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); };
  }, [id]);

  const download = () => {
    if (!pdfUrl || !invoice) return;
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = invoice.invoice_number.replace(/\//g, "_") + ".pdf";
    link.click();
  };

  const print = () => {
    if (!pdfUrl) return;
    const w = window.open(pdfUrl, "_blank");
    if (w) w.addEventListener("load", () => w.print());
  };

  const shareLink = `${window.location.origin.replace(":3000", "")}/share/${invoice?.share_token}`;
  const publicPdfUrl = `${API}/public/invoices/${invoice?.share_token}/pdf`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      toast.success("Link copied");
    } catch { toast.error("Copy failed"); }
  };

  const whatsApp = () => {
    if (!invoice) return;
    const text = `Hi ${invoice.customer?.name || ""},%0A%0AHere is your invoice ${invoice.invoice_number} (₹${invoice.calculation?.total}). View / Download: ${publicPdfUrl}`;
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  const sendEmail = async () => {
    if (!emailTo) { toast.error("Email address required"); return; }
    setSending(true);
    try {
      const res = await api.post(`/invoices/${id}/email`, { to: emailTo, message: emailMessage || undefined });
      if (res?.data?.warning) {
        toast.success(`Email sent to ${emailTo}`, { description: res.data.warning, duration: 8000 });
      } else {
        toast.success(`Email sent to ${emailTo}`);
      }
      setEmailOpen(false);
      await loadEmailLog();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Email failed — make sure RESEND_API_KEY is set in backend .env";
      toast.error(msg);
      await loadEmailLog();
    } finally { setSending(false); }
  };

  const duplicate = () => {
    if (!invoice) return;
    sessionStorage.setItem("invoice-duplicate", JSON.stringify({
      customer: invoice.customer,
      delivery_address: invoice.delivery_address,
      items: invoice.items,
      options: invoice.options,
      discount_percent: invoice.discount_percent,
      shipping_amount: invoice.shipping_amount,
      show_upi_qr: invoice.show_upi_qr,
      payment_mode: invoice.payment_mode,
      payment_terms: invoice.payment_terms,
      invoice_type: invoice.invoice_type,
    }));
    navigate("/invoices/new");
  };

  const submitMarkPaid = async () => {
    const hasTxn = (paidForm.transaction_id || "").trim().length > 0;
    if (!hasTxn && !paidFile) {
      toast.error("Provide a transaction ID or a payment screenshot");
      return;
    }
    setMarkingPaid(true);
    try {
      const fd = new FormData();
      if (hasTxn) fd.append("transaction_id", paidForm.transaction_id.trim());
      if (paidForm.payment_date) fd.append("payment_date", paidForm.payment_date);
      if (paidFile) fd.append("screenshot", paidFile);
      const { data } = await api.post(`/invoices/${id}/mark-paid`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setInvoice(data);
      setPaidOpen(false);
      setPaidFile(null);
      setPaidForm({ transaction_id: "", payment_date: "" });
      toast.success("Invoice marked as paid");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to mark paid");
    } finally {
      setMarkingPaid(false);
    }
  };

  const unmarkPaid = async () => {
    if (!window.confirm("Revert this invoice back to unpaid?")) return;
    setUnmarkBusy(true);
    try {
      const { data } = await api.post(`/invoices/${id}/mark-unpaid`);
      setInvoice(data);
      toast.success("Reverted to unpaid");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setUnmarkBusy(false);
    }
  };

  const screenshotUrl = invoice?.payment_screenshot_path
    ? `${API}/invoices/${id}/payment-screenshot`
    : "";

  const remove = async () => {
    if (!window.confirm("Delete this invoice?")) return;
    try {
      await api.delete(`/invoices/${id}`);
      toast.success("Deleted");
      navigate("/invoices");
    } catch { toast.error("Delete failed"); }
  };

  if (loading || !invoice) {
    return <div className="p-8 text-slate-500">Loading invoice…</div>;
  }
  const c = invoice.calculation || {};

  return (
    <div data-testid="invoice-view">
      <div className="glass-header sticky top-0 z-10 px-8 py-5">
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={() => navigate("/invoices")} className="p-2 rounded-md hover:bg-slate-100 text-slate-600" data-testid="back-to-list"><ArrowLeft size={18} /></button>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Invoice</div>
            <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 font-mono">{invoice.invoice_number}</h1>
          </div>
          <button onClick={print} className="inline-flex items-center gap-2 px-3 h-10 rounded-md border border-slate-200 bg-white text-slate-700 text-sm font-medium hover:bg-slate-50" data-testid="print-button">
            <Printer size={16} /> Print
          </button>
          <button onClick={copyLink} className="inline-flex items-center gap-2 px-3 h-10 rounded-md border border-slate-200 bg-white text-slate-700 text-sm font-medium hover:bg-slate-50" data-testid="copy-link-button">
            <ShareNetwork size={16} /> Copy Link
          </button>
          <button onClick={whatsApp} className="inline-flex items-center gap-2 px-3 h-10 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700 text-sm font-medium hover:bg-emerald-100" data-testid="whatsapp-button">
            <WhatsappLogo size={16} weight="fill" /> WhatsApp
          </button>
          <button onClick={() => setEmailOpen(true)} className="inline-flex items-center gap-2 px-3 h-10 rounded-md border border-slate-200 bg-white text-slate-700 text-sm font-medium hover:bg-slate-50" data-testid="email-button">
            <EnvelopeSimple size={16} /> Email
          </button>
          {invoice.paid ? (
            <button
              onClick={unmarkPaid}
              disabled={unmarkBusy}
              className="inline-flex items-center gap-2 px-3 h-10 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700 text-sm font-medium hover:bg-emerald-100 disabled:opacity-50"
              data-testid="unmark-paid-button"
              title="Revert payment status"
            >
              <ArrowCounterClockwise size={16} /> Unmark Paid
            </button>
          ) : (
            <button
              onClick={() => setPaidOpen(true)}
              className="inline-flex items-center gap-2 px-3 h-10 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700 text-sm font-medium hover:bg-emerald-100"
              data-testid="mark-paid-button"
            >
              <CheckCircle size={16} weight="duotone" /> Mark as Paid
            </button>
          )}
          <button onClick={duplicate} className="inline-flex items-center gap-2 px-3 h-10 rounded-md border border-slate-200 bg-white text-slate-700 text-sm font-medium hover:bg-slate-50" data-testid="duplicate-button">
            <CopyIcon size={16} /> Duplicate
          </button>
          {!invoice.paid && (
            <button
              onClick={() => navigate(`/invoices/${id}/edit`)}
              className="inline-flex items-center gap-2 px-3 h-10 rounded-md border border-blue-200 bg-blue-50 text-blue-700 text-sm font-medium hover:bg-blue-100"
              data-testid="edit-invoice-button"
              title="Edit this invoice"
            >
              <PencilSimple size={16} /> Edit
            </button>
          )}
          <button
            onClick={openHistory}
            className="inline-flex items-center gap-2 px-3 h-10 rounded-md border border-slate-200 bg-white text-slate-700 text-sm font-medium hover:bg-slate-50"
            data-testid="history-button"
            title="View edit history"
          >
            <ClockCounterClockwise size={16} /> History
          </button>
          <button onClick={download} className="inline-flex items-center gap-2 px-4 h-10 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700" data-testid="download-button">
            <DownloadSimple size={16} weight="bold" /> Download PDF
          </button>
          <button onClick={remove} className="inline-flex items-center gap-2 px-3 h-10 rounded-md border border-red-100 bg-white text-red-600 text-sm font-medium hover:bg-red-50" data-testid="delete-button">
            <Trash size={16} />
          </button>
        </div>
      </div>

      <div className="p-8 space-y-5">
        {invoice.paid ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4 flex items-center gap-3" data-testid="paid-banner">
            <div className="w-10 h-10 rounded-full bg-emerald-600 text-white flex items-center justify-center">
              <CheckCircle size={22} weight="fill" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-emerald-900">Payment recorded</div>
              <div className="text-xs text-emerald-700 mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                {invoice.transaction_id && (
                  <span className="inline-flex items-center gap-1.5">
                    <Receipt size={12} />
                    <span className="font-mono">{invoice.transaction_id}</span>
                  </span>
                )}
                {invoice.payment_date && (
                  <span>· paid on {invoice.payment_date}</span>
                )}
                {invoice.paid_by && (
                  <span>· by {invoice.paid_by}</span>
                )}
                {screenshotUrl && (
                  <a
                    href={screenshotUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 underline text-emerald-800 hover:text-emerald-900"
                    data-testid="view-payment-screenshot"
                  >
                    <UploadSimple size={12} /> View screenshot
                  </a>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4 flex items-center gap-3" data-testid="success-banner">
            <div className="w-8 h-8 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold">✓</div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-emerald-900">Invoice generated</div>
              <div className="text-xs text-emerald-700">{invoice.invoice_number} · {invoice.date} · ₹{fmt(c.total)}</div>
            </div>
            <button onClick={download} className="inline-flex items-center gap-2 px-4 h-9 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700">
              <DownloadSimple size={14} weight="bold" /> Download Invoice
            </button>
          </div>
        )}

        {emailLog.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-lg" data-testid="email-log-panel">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
              <EnvelopeSimple size={16} weight="duotone" className="text-slate-500" />
              <div className="text-sm font-semibold text-slate-900">Email history</div>
              <div className="text-xs text-slate-500">· {emailLog.length} send{emailLog.length === 1 ? "" : "s"}</div>
            </div>
            <div className="divide-y divide-slate-100 max-h-56 overflow-y-auto">
              {emailLog.map((row) => (
                <div key={row.id} className="px-5 py-3 flex items-center gap-3 text-sm" data-testid={`email-log-${row.id}`}>
                  <span
                    className={
                      "inline-flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-bold " +
                      (row.status === "sent" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700")
                    }
                  >
                    {row.status === "sent" ? "✓" : "!"}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-slate-900 font-medium truncate">{row.to}</div>
                    <div className="text-xs text-slate-500 truncate">
                      from {row.from} · {new Date(row.sent_at).toLocaleString()}
                      {row.warning && <span className="text-amber-600"> · domain fallback</span>}
                      {row.error && <span className="text-red-600"> · {row.error}</span>}
                    </div>
                  </div>
                  {row.email_id && (
                    <code className="text-[10px] text-slate-400 font-mono shrink-0">{row.email_id.slice(0, 8)}</code>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          {pdfUrl ? (
            <iframe title="invoice-pdf" src={pdfUrl} className="w-full" style={{ height: "calc(100vh - 220px)" }} data-testid="pdf-iframe" />
          ) : (
            <div className="p-10 text-center text-slate-500">Generating PDF preview…</div>
          )}
        </div>
      </div>

      {emailOpen && (
        <Dialog open={emailOpen} onOpenChange={(o) => !o && setEmailOpen(false)}>
          <DialogContent className="sm:max-w-md" data-testid="email-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Email Invoice</DialogTitle>
              <DialogDescription className="text-sm text-slate-500">
                We&apos;ll send the PDF as an attachment via Resend.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">To</Label>
                <Input type="email" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} className="mt-1.5" data-testid="email-to" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Message (optional)</Label>
                <Textarea rows={4} value={emailMessage} onChange={(e) => setEmailMessage(e.target.value)} className="mt-1.5" placeholder="Default friendly message will be used if left blank." />
              </div>
            </div>
            <DialogFooter>
              <button onClick={() => setEmailOpen(false)} className="px-4 h-10 rounded-md border border-slate-200 bg-white text-slate-700 text-sm font-medium hover:bg-slate-50">Cancel</button>
              <button onClick={sendEmail} disabled={sending} className="px-4 h-10 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-60" data-testid="send-email">
                {sending ? "Sending…" : "Send Email"}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {historyOpen && (
        <Dialog open={historyOpen} onOpenChange={(o) => { if (!o) { setHistoryOpen(false); setSnapshotView(null); } }}>
          <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="history-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <ClockCounterClockwise size={18} weight="duotone" /> Edit History
              </DialogTitle>
              <DialogDescription className="text-sm text-slate-500">
                {versions.length === 0
                  ? "No edits yet. This invoice hasn't been modified since it was created."
                  : `${versions.length} previous version${versions.length === 1 ? "" : "s"} on file. Each save creates a new snapshot.`}
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto -mx-6 px-6 py-1">
              {historyLoading && (
                <div className="text-sm text-slate-500 py-10 text-center">Loading…</div>
              )}
              {!historyLoading && versions.length === 0 && (
                <div className="text-center py-10 text-slate-400 text-sm border border-dashed border-slate-200 rounded-md">
                  History will appear here after the first edit.
                </div>
              )}
              {!historyLoading && versions.length > 0 && (
                <ul className="divide-y divide-slate-100 border border-slate-200 rounded-md">
                  {versions.map((v) => (
                    <li key={v.id} className="px-4 py-3 flex items-center justify-between gap-3" data-testid={`history-row-${v.version_no}`}>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-slate-900">
                          Version v{v.version_no}
                          <span className="ml-2 text-xs font-normal text-slate-500">
                            {v.snapshot_invoice_number}
                          </span>
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {new Date(v.edited_at).toLocaleString()} · {v.edited_by}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <div className="text-xs text-slate-400 uppercase tracking-wider">Total then</div>
                          <div className="text-sm font-semibold text-slate-900">
                            ₹{Number(v.snapshot_total || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                          </div>
                        </div>
                        <button
                          onClick={() => viewSnapshot(v.version_no)}
                          className="inline-flex items-center gap-1.5 px-3 h-8 rounded-md border border-slate-200 bg-white text-slate-700 text-xs font-medium hover:bg-slate-50"
                          data-testid={`view-snapshot-${v.version_no}`}
                        >
                          View
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              {snapshotView && (
                <div className="mt-4 border border-slate-200 rounded-md bg-slate-50 overflow-hidden" data-testid="snapshot-detail">
                  <div className="px-4 py-2 border-b border-slate-200 bg-white flex items-center justify-between">
                    <div className="text-sm font-semibold text-slate-900">
                      v{snapshotView.version_no} snapshot
                      <span className="ml-2 text-xs font-normal text-slate-500">
                        {snapshotView.snapshot_invoice_number} · {new Date(snapshotView.edited_at).toLocaleString()}
                      </span>
                    </div>
                    <button
                      onClick={() => setSnapshotView(null)}
                      className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100"
                      data-testid="close-snapshot"
                    >
                      <X size={14} />
                    </button>
                  </div>
                  <div className="p-4 space-y-3 text-sm">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500">Customer</div>
                        <div className="font-medium text-slate-900">{snapshotView.snapshot?.customer?.name || "—"}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500">Total</div>
                        <div className="font-medium text-slate-900">
                          ₹{Number(snapshotView.snapshot?.calculation?.total || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500">Discount</div>
                        <div className="font-medium text-slate-900">{snapshotView.snapshot?.discount_percent || 0}%</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500">Shipping</div>
                        <div className="font-medium text-slate-900">
                          ₹{Number(snapshotView.snapshot?.shipping_amount || 0).toLocaleString("en-IN")}
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Line Items</div>
                      <div className="border border-slate-200 rounded-md bg-white overflow-hidden">
                        <table className="w-full text-xs">
                          <thead className="bg-slate-50 text-slate-500">
                            <tr>
                              <th className="px-2 py-1.5 text-left">Description</th>
                              <th className="px-2 py-1.5 text-right">Qty</th>
                              <th className="px-2 py-1.5 text-right">Rate</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {(snapshotView.snapshot?.items || []).map((it, i) => (
                              <tr key={i}>
                                <td className="px-2 py-1.5 text-slate-700">{it.description}</td>
                                <td className="px-2 py-1.5 text-right text-slate-700">{it.qty}</td>
                                <td className="px-2 py-1.5 text-right text-slate-700">₹{Number(it.rate || 0).toLocaleString("en-IN")}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <button
                onClick={() => { setHistoryOpen(false); setSnapshotView(null); }}
                className="px-4 h-10 rounded-md border border-slate-200 bg-white text-slate-700 text-sm font-medium hover:bg-slate-50"
                data-testid="history-close"
              >
                Close
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {paidOpen && (
        <Dialog open={paidOpen} onOpenChange={(o) => !o && setPaidOpen(false)}>
          <DialogContent className="sm:max-w-md" data-testid="mark-paid-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Mark Invoice as Paid</DialogTitle>
              <DialogDescription className="text-sm text-slate-500">
                Provide a transaction ID, a payment screenshot, or both.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">
                  Transaction ID
                </Label>
                <Input
                  type="text"
                  value={paidForm.transaction_id}
                  onChange={(e) => setPaidForm({ ...paidForm, transaction_id: e.target.value })}
                  placeholder="e.g. UPI/NEFT/RTGS reference"
                  className="mt-1.5"
                  data-testid="paid-txn-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">
                  Payment Date
                </Label>
                <Input
                  type="date"
                  value={paidForm.payment_date}
                  onChange={(e) => setPaidForm({ ...paidForm, payment_date: e.target.value })}
                  className="mt-1.5"
                  data-testid="paid-date-input"
                />
                <p className="text-[10px] text-slate-400 mt-1">Defaults to today if left blank.</p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">
                  Payment Screenshot (optional)
                </Label>
                {paidFilePreview ? (
                  <div className="mt-2 relative border border-slate-200 rounded-md overflow-hidden bg-slate-50">
                    <img
                      src={paidFilePreview}
                      alt="payment preview"
                      className="max-h-48 mx-auto"
                      data-testid="paid-screenshot-preview"
                    />
                    <button
                      onClick={() => setPaidFile(null)}
                      className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white border border-slate-200 text-slate-600 flex items-center justify-center hover:bg-slate-100"
                      data-testid="paid-screenshot-clear"
                      type="button"
                    >
                      <X size={14} />
                    </button>
                    <div className="px-3 py-2 text-xs text-slate-500 bg-white border-t border-slate-100 truncate">
                      {paidFile?.name} · {(paidFile?.size / 1024).toFixed(1)} KB
                    </div>
                  </div>
                ) : (
                  <label
                    className="mt-2 flex flex-col items-center justify-center gap-2 px-4 py-6 border-2 border-dashed border-slate-200 rounded-md text-slate-500 text-sm cursor-pointer hover:bg-slate-50 hover:border-slate-300"
                    data-testid="paid-screenshot-dropzone"
                  >
                    <UploadSimple size={20} className="text-slate-400" />
                    <span>Click to upload</span>
                    <span className="text-[10px] text-slate-400">PNG / JPG / WEBP · max 8 MB</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) setPaidFile(f);
                      }}
                      className="hidden"
                      data-testid="paid-screenshot-input"
                    />
                  </label>
                )}
              </div>
            </div>
            <DialogFooter>
              <button
                onClick={() => setPaidOpen(false)}
                className="px-4 h-10 rounded-md border border-slate-200 bg-white text-slate-700 text-sm font-medium hover:bg-slate-50"
                data-testid="paid-cancel"
              >
                Cancel
              </button>
              <button
                onClick={submitMarkPaid}
                disabled={markingPaid}
                className="inline-flex items-center gap-2 px-4 h-10 rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60"
                data-testid="paid-submit"
              >
                <CheckCircle size={14} weight="fill" />
                {markingPaid ? "Saving…" : "Mark as Paid"}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
